import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/lib/authContext";
import {
  MessageSquare,
  Ticket,
  BookOpen,
  ArrowRight,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  ScanSearch,
} from "lucide-react";

const statusColor = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/tickets/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/tickets").then((r) => setTickets(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="dashboard-home">
      <div className="mb-12 fade-up">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">{greeting}</div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-zinc-900">
          Hi, {user?.name?.split(" ")[0]}. <span className="text-zinc-400">What do you need help with?</span>
        </h1>
      </div>

      {/* Big quick actions */}
      <div className="grid md:grid-cols-3 gap-5 mb-12">
        <Link
          to="/app/chat"
          className="md:col-span-2 group bg-zinc-900 text-white p-8 rounded-3xl relative overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all"
          data-testid="quick-chat"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl" />
          <MessageSquare className="w-10 h-10 mb-6 text-blue-400" strokeWidth={1.5} />
          <h2 className="font-display text-3xl font-bold mb-2">Chat with the AI Assistant</h2>
          <p className="text-zinc-400 max-w-md">Type your IT problem in plain English — get step-by-step instructions in seconds.</p>
          <div className="mt-6 inline-flex items-center gap-1 text-blue-400 font-semibold text-sm group-hover:gap-2 transition-all">
            Start chatting <ArrowRight className="w-4 h-4" />
          </div>
        </Link>

        <Link
          to="/app/tickets/new"
          className="group bg-blue-600 text-white p-8 rounded-3xl hover:shadow-2xl hover:-translate-y-1 transition-all relative overflow-hidden"
          data-testid="quick-new-ticket"
        >
          <Plus className="w-10 h-10 mb-6" strokeWidth={1.5} />
          <h2 className="font-display text-2xl font-bold mb-2">File a ticket</h2>
          <p className="text-blue-100 text-sm">Need a human? Submit a ticket and we'll route it to IT.</p>
          <div className="mt-6 inline-flex items-center gap-1 text-white font-semibold text-sm group-hover:gap-2 transition-all">
            New ticket <ArrowRight className="w-4 h-4" />
          </div>
        </Link>
      </div>

      {/* Screenshot diagnostics banner */}
      <Link
        to="/app/chat"
        className="group block bg-blue-600 text-white p-7 rounded-3xl mb-12 relative overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all"
        data-testid="quick-diagnose"
      >
        <div className="absolute -top-24 -right-16 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center shrink-0">
              <ScanSearch className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold mb-1">Upload Screenshot for Instant Help</h2>
              <p className="text-blue-100 text-sm max-w-lg">Snap the error — Outlook, Microsoft 365 login, printer codes, Wi-Fi — and AI gives you the fix instantly. No ticket needed.</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-6 py-3 rounded-full text-sm group-hover:gap-3 transition-all shrink-0">
            Upload screenshot <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {[
          { label: "All tickets", value: stats.total, icon: Ticket, color: "text-zinc-900" },
          { label: "Open", value: stats.open, icon: AlertCircle, color: "text-blue-600" },
          { label: "In progress", value: stats.in_progress, icon: Clock, color: "text-amber-600" },
          { label: "Resolved", value: stats.resolved, icon: CheckCircle2, color: "text-emerald-600" },
        ].map((s) => (
          <div key={s.label} className="bg-white p-5 rounded-2xl border border-zinc-200" data-testid={`stat-${s.label.toLowerCase().replace(" ", "-")}`}>
            <s.icon className={`w-5 h-5 mb-3 ${s.color}`} strokeWidth={2} />
            <div className="font-display text-3xl font-black text-zinc-900">{s.value}</div>
            <div className="text-xs text-zinc-500 font-semibold uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent tickets */}
      <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden">
        <div className="p-6 flex items-center justify-between border-b border-zinc-100">
          <h3 className="font-display font-bold text-xl text-zinc-900">Recent tickets</h3>
          <Link to="/app/tickets" className="text-sm font-semibold text-blue-600 hover:underline" data-testid="view-all-tickets">
            View all →
          </Link>
        </div>
        {tickets.length === 0 ? (
          <div className="p-12 text-center" data-testid="no-tickets">
            <Ticket className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500 mb-4">No tickets yet. Try the AI assistant first — most issues are solved instantly.</p>
            <Link
              to="/app/chat"
              className="inline-flex items-center gap-1 bg-zinc-900 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:bg-zinc-800 transition"
            >
              Start a chat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {tickets.map((t) => (
              <Link
                key={t.id}
                to={`/app/tickets/${t.id}`}
                className="flex items-center justify-between p-5 hover:bg-zinc-50 transition"
                data-testid={`recent-ticket-${t.id}`}
              >
                <div className="flex-1 min-w-0 pr-4">
                  <div className="font-bold text-zinc-900 truncate">{t.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {new Date(t.created_at).toLocaleString()} · {t.category}
                  </div>
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border ${statusColor[t.status]}`}>
                  {t.status.replace("_", " ")}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* KB shortcut */}
      <Link
        to="/app/kb"
        className="mt-8 block bg-white border border-zinc-200 rounded-2xl p-6 hover:border-zinc-900 hover:shadow-md transition-all"
        data-testid="kb-shortcut"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-zinc-900" />
            </div>
            <div>
              <div className="font-display font-bold text-lg text-zinc-900">Browse the knowledge base</div>
              <div className="text-sm text-zinc-500">Self-serve answers for the most common IT issues</div>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-zinc-400" />
        </div>
      </Link>
    </div>
  );
}
